/**
 * Thin fetch wrapper used by all TanStack Query hooks.
 *
 * Why this layer exists:
 *  - One place to add the `Idempotency-Key` header for mutating verbs
 *    (per INTEG-004; mock-api enforces it for POST /documents).
 *  - One place to translate `application/problem+json` errors so the UI
 *    can render the `title` / `detail` consistently.
 *  - One place to thread the trace-id from response headers into telemetry.
 *  - One place to switch base URL between Vite proxy (dev) and ngrok
 *    tunnel (deploy) — driven by /config.json (see runtimeConfig.ts).
 *  - One place to send `ngrok-skip-browser-warning` so the ngrok free-tier
 *    interstitial doesn't return HTML to JSON-parsing callers.
 *
 * Owner: Claude 3 (Frontend & UX).
 */

import { getRuntimeConfig } from './runtimeConfig';

export class ApiError extends Error {
  status: number;
  title: string;
  detail?: string;
  traceId?: string;

  constructor(status: number, title: string, detail?: string, traceId?: string) {
    super(detail ? `${title}: ${detail}` : title);
    this.status = status;
    this.title = title;
    this.detail = detail;
    this.traceId = traceId;
  }
}

interface RequestOpts {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  body?: unknown;
  idempotencyKey?: string;
  query?: Record<string, string | number | undefined>;
  signal?: AbortSignal;
  headers?: Record<string, string>;
}

export async function apiFetch<T>(path: string, opts: RequestOpts = {}): Promise<T> {
  const { method = 'GET', body, idempotencyKey, query, signal, headers: extraHeaders } = opts;

  // Runtime config decides where the API lives.
  //   - dev: api_base = ""  → URL becomes /api/v1/... → Vite proxy → mock-api on :8081
  //   - deploy: api_base = "https://xxx.ngrok-free.dev" → goes directly to the tunnel
  const apiBase = getRuntimeConfig().api_base;

  // Build the full URL. When api_base is empty we use window.location.origin
  // (same-origin / Vite proxy); when set we use the tunnel origin.
  const origin = apiBase || window.location.origin;
  const url = new URL(path.startsWith('/') ? path : `/${path}`, origin);
  if (query) {
    for (const [k, v] of Object.entries(query)) {
      if (v === undefined || v === '') continue;
      url.searchParams.set(k, String(v));
    }
  }

  // V26 multi-site — inject the user's active site scope as ?site=<uuid>
  // for any GET that doesn't already specify one. Endpoints whose backing
  // tables don't have site_id (e.g. /sites, /retention/policies) simply
  // ignore the parameter on the server side. This keeps list pages scoped
  // automatically when the user picks a site from the topbar switcher.
  if (
    method === 'GET' &&
    !url.searchParams.has('site') &&
    typeof window !== 'undefined' &&
    window.localStorage
  ) {
    try {
      const raw = window.localStorage.getItem('edms.site-scope');
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed?.kind === 'site' && typeof parsed.siteId === 'string') {
          url.searchParams.set('site', parsed.siteId);
        }
      }
    } catch {
      // Quota / private-mode / parse failure — ignore.
    }
  }

  // Pull session tokens + tenant from sessionStorage. The real Spring Boot
  // services (workflow-svc, document-svc, org-svc, capa-svc, deviation-svc,
  // oos-svc, risk-svc, audit, change-requests) require an Authorization
  // bearer token + X-EDMS-Tenant-Id header. The prod-proxy forwards both.
  const access   = (typeof window !== 'undefined' && window.sessionStorage)
    ? window.sessionStorage.getItem('edms.session.access') : null;
  const tenantId = (typeof window !== 'undefined' && window.sessionStorage)
    ? window.sessionStorage.getItem('edms.session.tenantId') : null;

  const headers: Record<string, string> = {
    Accept: 'application/json',
    ...(access ? { Authorization: `Bearer ${access}` } : {}),
    ...(tenantId ? { 'X-EDMS-Tenant-Id': tenantId } : {}),
    ...(extraHeaders ?? {}),
  };
  if (body !== undefined) headers['Content-Type'] = 'application/json';
  if (method !== 'GET' && idempotencyKey) {
    headers['Idempotency-Key'] = idempotencyKey;
  }

  // When apiBase is set, hit it directly (CORS handled by mock-api).
  // When apiBase is empty, send a same-origin relative path so Vite proxies it.
  const target = apiBase ? url.toString() : url.pathname + url.search;

  const res = await fetch(target, {
    method,
    headers,
    body: body !== undefined ? JSON.stringify(body) : undefined,
    signal,
    credentials: apiBase ? 'omit' : 'same-origin',
  });

  if (!res.ok) {
    let title = res.statusText;
    let detail: string | undefined;
    let traceId = res.headers.get('x-trace-id') ?? undefined;
    try {
      const problem = await res.json();
      title = problem.title ?? title;
      detail = problem.detail;
      traceId = problem.traceId ?? traceId;
    } catch {
      // not JSON; leave defaults
    }
    throw new ApiError(res.status, title, detail, traceId);
  }

  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

/**
 * Build the standard header set used by every API call in the SPA.
 *
 * Exported so the AI advisory / cluster components and other places that
 * call `fetch()` directly (where apiFetch's TanStack-Query plumbing would
 * be overkill) can stay consistent without duplicating the logic.
 *
 *   - Accept: application/json
 *   - ngrok-skip-browser-warning: bypass the free-tier interstitial
 *   - Authorization: Bearer <access> if we have a session
 *   - X-EDMS-Tenant-Id: from session if we have a tenant
 *   - Content-Type: application/json when caller is sending a JSON body
 */
export function buildHeaders(opts: { json?: boolean; extra?: Record<string, string> } = {}): Record<string, string> {
  const access = (typeof window !== 'undefined' && window.sessionStorage)
    ? window.sessionStorage.getItem('edms.session.access') : null;
  const tenantId = (typeof window !== 'undefined' && window.sessionStorage)
    ? window.sessionStorage.getItem('edms.session.tenantId') : null;
  return {
    Accept: 'application/json',
    ...(opts.json ? { 'Content-Type': 'application/json' } : {}),
    ...(access ? { Authorization: `Bearer ${access}` } : {}),
    ...(tenantId ? { 'X-EDMS-Tenant-Id': tenantId } : {}),
    ...(opts.extra ?? {}),
  };
}

/**
 * Resolve the full URL for a relative API path.
 *
 * Also auto-injects `?site=<uuid>` for GET-ish endpoints when the user has
 * selected a single-site scope via the SiteScopeSwitcher. This makes raw
 * `fetch(apiUrl(...))` callers (legacy CAPA / deviation / equipment / etc
 * workbenches that don't go through apiFetch) site-aware for free.
 *
 * Skipped automatically when `path` already includes `?site=` so callers
 * can override.
 */
export function apiUrl(path: string): string {
  const base = getRuntimeConfig().api_base;
  // Start with the absolute/relative URL.
  let resolved = base
    ? new URL(path.startsWith('/') ? path : `/${path}`, base).toString()
    : path.startsWith('/')
    ? path
    : `/${path}`;

  // Inject the active site scope from localStorage so any list endpoint
  // that supports `?site=` gets filtered server-side. No-op when scope is
  // 'all' or when caller already specified `?site=`.
  if (typeof window !== 'undefined' && window.localStorage && !/[?&]site=/.test(resolved)) {
    try {
      const raw = window.localStorage.getItem('edms.site-scope');
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed?.kind === 'site' && typeof parsed.siteId === 'string') {
          const sep = resolved.includes('?') ? '&' : '?';
          resolved += `${sep}site=${encodeURIComponent(parsed.siteId)}`;
        }
      }
    } catch {
      // Ignore — auth/private-mode/storage quota.
    }
  }
  return resolved;
}

export function newIdempotencyKey(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) return crypto.randomUUID();
  return `id-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

/**
 * Trigger a browser file download for an *authenticated* API resource.
 *
 * Why this exists: a new-tab `window.open(url)` or a plain `<a href download>`
 * navigates the browser, which does NOT attach the `Authorization: Bearer`
 * header — so any token-protected download 401s. Here we fetch the bytes with
 * `buildHeaders()` (token + tenant), turn the response into a Blob, and click a
 * transient object-URL anchor. Works for GET endpoints (template artifact) and
 * for inline-content responses alike. (drafts/61 F0-DL.)
 *
 * @param path  API path (e.g. `/api/v1/sop-templates/{id}/artifact`).
 * @param fallbackFilename used when the server sends no Content-Disposition.
 */
export async function downloadAuthed(path: string, fallbackFilename = 'download'): Promise<void> {
  const res = await fetch(apiUrl(path), { headers: buildHeaders() });
  if (!res.ok) {
    let title = `Download failed (${res.status})`;
    let detail: string | undefined;
    try {
      const body = (await res.json()) as { title?: string; detail?: string };
      title = body.title ?? title;
      detail = body.detail;
    } catch { /* non-JSON error body */ }
    throw new ApiError(res.status, title, detail, res.headers.get('x-trace-id') ?? undefined);
  }

  // Prefer the server-provided filename from Content-Disposition.
  let filename = fallbackFilename;
  const cd = res.headers.get('content-disposition');
  const m = cd && /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(cd);
  if (m && m[1]) filename = decodeURIComponent(m[1].trim());

  const blob = await res.blob();
  const objUrl = URL.createObjectURL(blob);
  try {
    const a = document.createElement('a');
    a.href = objUrl;
    a.download = filename;
    a.rel = 'noopener';
    document.body.appendChild(a);
    a.click();
    a.remove();
  } finally {
    // Revoke on the next tick so the click has a chance to start the download.
    setTimeout(() => URL.revokeObjectURL(objUrl), 1500);
  }
}

/**
 * Download a client-built file (e.g. a CSV returned inline in a JSON response).
 * Same transient-anchor pattern as {@link downloadAuthed}, but the content is
 * already in memory so no fetch is needed. (drafts/61 F0-DL.)
 */
export function downloadTextFile(content: string, filename: string, mime = 'text/csv;charset=utf-8'): void {
  const blob = new Blob([content], { type: mime });
  const objUrl = URL.createObjectURL(blob);
  try {
    const a = document.createElement('a');
    a.href = objUrl;
    a.download = filename;
    a.rel = 'noopener';
    document.body.appendChild(a);
    a.click();
    a.remove();
  } finally {
    setTimeout(() => URL.revokeObjectURL(objUrl), 1500);
  }
}
